import type {
  BlueprintRunDecisionPackDto,
  BlueprintRunDto,
  BlueprintRunRuntimeStatusDto,
  BlueprintRunVerificationPlanDto,
} from './blueprint-runs';
import type { BlueprintPackDto } from './blueprint-platform';
import type { DecisionLedgerDecisionStatusDto } from './decision-ledger';
import type { BlueprintProofStateDto } from './blueprint-proof-state';
import {
  isBlueprintProofState,
  meetsBlueprintProofState,
} from './blueprint-proof-state';
import type { BlueprintRuntimeProvenanceDto } from './blueprint-fixture-guard';
import {
  assertNoBlueprintFixtureLeak,
  detectBlueprintFixtureLeak,
  isBlueprintProvenanceSourceClass,
  isBlueprintRunMode,
} from './blueprint-fixture-guard';

export const TRANSFORMATION_BLUEPRINT_PACKAGE_SCHEMA_VERSION = 'transformation-blueprint-package/v1';

export type TransformationBlueprintPackageV1Dto = {
  schemaVersion: typeof TRANSFORMATION_BLUEPRINT_PACKAGE_SCHEMA_VERSION;
  packageId: string;
  pack: BlueprintPackDto;
  runId: string;
  run: BlueprintRunDto;
  runtimeStatus: BlueprintRunRuntimeStatusDto;
  decisionPack: BlueprintRunDecisionPackDto | null;
  verificationPlan: BlueprintRunVerificationPlanDto | null;
  ledgerDecisionStatus: DecisionLedgerDecisionStatusDto | null;
  proofState: BlueprintProofStateDto;
  provenance: BlueprintRuntimeProvenanceDto;
};

export type TransformationBlueprintPackageStateDto =
  | 'blocked'
  | DecisionLedgerDecisionStatusDto;

const DECISION_LEDGER_STATUSES: readonly DecisionLedgerDecisionStatusDto[] = [
  'draft',
  'approved_unactivated',
  'active',
  'archived',
] as const;

export function deriveTransformationBlueprintPackageState(
  pkg: Pick<
    TransformationBlueprintPackageV1Dto,
    'ledgerDecisionStatus' | 'pack' | 'proofState' | 'provenance'
  >,
): TransformationBlueprintPackageStateDto {
  if (detectBlueprintFixtureLeak(pkg.provenance)) {
    return 'blocked';
  }

  const status = pkg.ledgerDecisionStatus ?? 'draft';
  if (status === 'draft' || status === 'archived') {
    return status;
  }

  if (!meetsBlueprintProofState(pkg.proofState, pkg.pack.readiness.minimumProofState)) {
    return 'blocked';
  }

  return status;
}

export function parseTransformationBlueprintPackageV1(
  input: unknown,
): TransformationBlueprintPackageV1Dto {
  const record = requireRecord(input, 'TransformationBlueprintPackageV1');
  const schemaVersion = requireString(record.schemaVersion, 'schemaVersion');
  if (schemaVersion !== TRANSFORMATION_BLUEPRINT_PACKAGE_SCHEMA_VERSION) {
    throw new Error(
      `Invalid TransformationBlueprintPackageV1 schemaVersion '${schemaVersion}'.`,
    );
  }

  const packageId = requireString(record.packageId, 'packageId');
  const pack = requireRecord(record.pack, 'pack');
  const run = requireRecord(record.run, 'run');
  const runtimeStatus = requireRecord(record.runtimeStatus, 'runtimeStatus');
  const runId = requireString(record.runId, 'runId');
  const runRunId = requireString(run.runId, 'run.runId');
  const runPackId = requireString(run.packId, 'run.packId');
  const packId = requireString(pack.packId, 'pack.packId');
  requireString(pack.packVersion, 'pack.packVersion');
  requireString(runtimeStatus.runId, 'runtimeStatus.runId');

  if (runId !== runRunId) {
    throw new Error(`TransformationBlueprintPackageV1 runId '${runId}' does not match run.runId '${runRunId}'.`);
  }
  if (runPackId !== packId) {
    throw new Error(`TransformationBlueprintPackageV1 run.packId '${runPackId}' does not match pack.packId '${packId}'.`);
  }

  const readiness = requireRecord(pack.readiness, 'pack.readiness');
  const minimumProofState = parseProofState(readiness.minimumProofState, 'pack.readiness.minimumProofState');
  const proofState = parseProofState(record.proofState, 'proofState');
  const provenance = parseBlueprintRuntimeProvenance(record.provenance);
  if (provenance.proofState !== proofState) {
    throw new Error(
      `TransformationBlueprintPackageV1 proofState '${proofState}' does not match provenance proofState '${provenance.proofState}'.`,
    );
  }

  const ledgerDecisionStatus = parseNullableDecisionLedgerStatus(record.ledgerDecisionStatus);
  const parsed = input as TransformationBlueprintPackageV1Dto;
  assertNoBlueprintFixtureLeak(provenance, {
    governedPath: `blueprint/runs/packages/${packageId}`,
    payload: input,
  });

  const packageState = deriveTransformationBlueprintPackageState({
    ledgerDecisionStatus,
    pack: {
      ...(pack as unknown as BlueprintPackDto),
      readiness: {
        ...(readiness as unknown as BlueprintPackDto['readiness']),
        minimumProofState,
      },
    },
    proofState,
    provenance,
  });
  if (
    (ledgerDecisionStatus === 'approved_unactivated' || ledgerDecisionStatus === 'active') &&
    packageState === 'blocked'
  ) {
    throw new Error(
      `TransformationBlueprintPackageV1 cannot carry '${ledgerDecisionStatus}' with proof state '${proofState}'.`,
    );
  }

  return parsed;
}

function parseProofState(value: unknown, path: string): BlueprintProofStateDto {
  if (!isBlueprintProofState(value)) {
    throw new Error(`Invalid Blueprint proof state at ${path}: ${String(value)}.`);
  }
  return value;
}

function parseBlueprintRuntimeProvenance(value: unknown): BlueprintRuntimeProvenanceDto {
  const record = requireRecord(value, 'provenance');
  const runMode = record.runMode;
  const sourceClass = record.sourceClass;
  if (!isBlueprintRunMode(runMode)) {
    throw new Error(`Invalid Blueprint run mode at provenance.runMode: ${String(runMode)}.`);
  }
  if (!isBlueprintProvenanceSourceClass(sourceClass)) {
    throw new Error(`Invalid Blueprint source class at provenance.sourceClass: ${String(sourceClass)}.`);
  }

  return {
    runMode,
    sourceClass,
    proofState: parseProofState(record.proofState, 'provenance.proofState'),
    objectId: requireString(record.objectId, 'provenance.objectId'),
  };
}

function parseNullableDecisionLedgerStatus(value: unknown): DecisionLedgerDecisionStatusDto | null {
  if (value == null) {
    return null;
  }
  if (typeof value === 'string' && DECISION_LEDGER_STATUSES.includes(value as DecisionLedgerDecisionStatusDto)) {
    return value as DecisionLedgerDecisionStatusDto;
  }
  throw new Error(`Invalid Decision Ledger status: ${String(value)}.`);
}

function requireRecord(value: unknown, path: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new Error(`TransformationBlueprintPackageV1 requires object at ${path}.`);
  }
  return value as Record<string, unknown>;
}

function requireString(value: unknown, path: string): string {
  if (typeof value !== 'string' || !value.trim()) {
    throw new Error(`TransformationBlueprintPackageV1 requires non-empty string at ${path}.`);
  }
  return value;
}
